const HEBREW_MONTHS = [
  'ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני',
  'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'
];

const ENGLISH_MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

function currentLanguage(): 'en' | 'he' {
  // i18n sets document.documentElement.lang on every language change, so we
  // can read it here without threading the locale through 38 call sites. The
  // explicit `language` arg below still wins when a caller passes one.
  if (typeof document === 'undefined') return 'en';
  return document.documentElement.lang === 'he' ? 'he' : 'en';
}

export function formatLocalTime(
  isoString: string,
  timezone: string,
  fmt: 'time' | 'datetime' | 'date' | 'datetime-full' | 'datetime-weekday' = 'time',
  language: 'en' | 'he' = currentLanguage(),
): string {
  const date = new Date(isoString);

  // For date formats, use simple format without year and dots
  if (fmt === 'date' || fmt === 'datetime' || fmt === 'datetime-full' || fmt === 'datetime-weekday') {
    const formatter = new Intl.DateTimeFormat('en-GB', {
      day: 'numeric',
      month: '2-digit',
      timeZone: timezone,
    });
    const parts = formatter.formatToParts(date);
    const partMap = Object.fromEntries(
      parts.map(({ type, value }) => [type, value])
    );

    const day = partMap.day;
    const monthNum = parseInt(partMap.month) - 1;

    const monthName = language === 'he'
      ? HEBREW_MONTHS[monthNum]
      : ENGLISH_MONTHS[monthNum];

    if (fmt === 'date') {
      return `${day} ${monthName}`;
    } else if (fmt === 'datetime' || fmt === 'datetime-full' || fmt === 'datetime-weekday') {
      // Add time for datetime formats
      const timeFormatter = new Intl.DateTimeFormat('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        timeZone: timezone,
      });
      const timeParts = timeFormatter.formatToParts(date);
      const timePartMap = Object.fromEntries(
        timeParts.map(({ type, value }) => [type, value])
      );
      const time = `${timePartMap.hour}:${timePartMap.minute}`;

      if (fmt === 'datetime-weekday') {
        // Prepend short weekday name (e.g. "Sat 5 Jun 14:30")
        const weekday = new Intl.DateTimeFormat(language === 'he' ? 'he-IL' : 'en-GB', {
          weekday: 'short',
          timeZone: timezone,
        }).format(date);
        return `${weekday} ${day} ${monthName} ${time}`;
      }

      return `${day} ${monthName} ${time}`;
    }
  }

  // For time-only format
  const formatter = new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: timezone,
  });
  return formatter.format(date);
}

/** Converts UTC ISO string to datetime-local format in the specified timezone */
export function isoToDatetimeLocal(isoString: string, timezone: string): string {
  const date = new Date(isoString);

  // Get the formatted date/time in the target timezone
  const parts = new Intl.DateTimeFormat('en-GB', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: timezone,
  }).formatToParts(date);

  const partMap = Object.fromEntries(
    parts.map(({ type, value }) => [type, value])
  );

  return `${partMap.year}-${partMap.month}-${partMap.day}T${partMap.hour}:${partMap.minute}`;
}

/** Returns true if isoString falls on today's calendar date in the given timezone */
export function isTodayInTz(isoString: string, timezone: string): boolean {
  const dayFmt = new Intl.DateTimeFormat('en-GB', {
    day: 'numeric', month: 'numeric', year: 'numeric', timeZone: timezone,
  });
  return dayFmt.format(new Date(isoString)) === dayFmt.format(new Date());
}
